import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "./ui/Button";
import InvestCheckModal from "./modal/InvestCheckModal";
import RejectWithCorrespondingModal from "./modal/RejectWithCorrespondingModal";
import RescanCheckModal from "./modal/RescanCheckModal"; // Import the new RescanCheckModal
import { useTicketContext } from "../context/TicketContext";
import ToastItem from "./ToastItem";
import storage from "../helpers/storage";

const BANK_ACCOUNTS_BY_BANK = {
  "PNC Bank": [
    "1028906087",
    "1028907629",
    "1028907637",
    "1028907645",
    "1028907653",
    "4227616799",
  ],
  "Wells Fargo Bank": ["4604153700"],
};

const BANK_OPTIONS = Object.keys(BANK_ACCOUNTS_BY_BANK);

// Constants for steps
const REVIEW_ASSIGNE = "review_assigne";
const INVEST_REJECT = "invest_reject";
const SHRED_CORRESPONDENCE = "shred_correspondence";

const ReviewProcessFormContainer = ({ lock }) => {
  const currentUserId = storage.getUserName();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const step = queryParams.get("step");
  const { checkProcessHandler, sendMessage, getTicketById, lockTicketById, ticket } = useTicketContext();
  const [formData, setFormData] = useState({});
  const [isRejectedWithShred, setIsRejectedWithShred] = useState(false);
  const [isInvestCheckModalOpen, updateInvestCheckModal] = useState(false);
  const [checkData, setCheckData] = useState({});
  const [isRejectWithCorrespondenceModalOpen, updateRejectWithCorrespondenceModal] = useState(false);
  const [isCheckReviewAvailable, setIsCheckReviewAvailable] = useState(false);
  const [isRescanModalOpen, setIsRescanModalOpen] = useState(false); // State for the Rescan modal
  const [isTicketCompleted, setIsTicketCompleted] = useState(false); // State to check if ticket is completed
  const [isAssignedToCurrentUser, setIsAssignedToCurrentUser] = useState(false); // State to check if ticket is assigned to current user
  const [allowedActions, setAllowedActions] = useState([]); // State to store allowed actions
  const [assignedTo, setAssignedTo] = useState(""); // State to store allowed actions

  let { id } = useParams();

  useEffect(() => {
    // Fetch ticket data only once when the component mounts
    const fetchTicketData = async () => {
      try {
        if (id) {
          let ticketData = {}
          if (lock === 'Y'){
            ticketData = await lockTicketById(id, 'N');
          } else {
            ticketData = await getTicketById(id);
          }

          setCheckData(ticketData);
          setAssignedTo(ticketData.assignedto)

          // Store allowed actions from ticket data
          if (ticketData && ticketData.allowedActions) {
            setAllowedActions(ticketData.allowedActions);
          }

          // Check if the ticket is completed
          if (ticketData && ticketData.status === "Completed") {
            setIsTicketCompleted(true);
          } else {
            // Check if the ticket is assigned to the current user
            if (ticketData && ticketData.assignedto === currentUserId) {
              setIsAssignedToCurrentUser(true);
            }

            if (ticketData && ticketData.workflowtype === "AARP Check Review") {
              setIsCheckReviewAvailable(true);
            }
          }
        }
      } catch (error) {
        toast.error("Error fetching ticket data");
      }
    };

    fetchTicketData();

    // Redirect to the default step if none is specified
    if (!step) {
      navigate(`${location.pathname}?step=${REVIEW_ASSIGNE}`, {
        replace: true,
      });
    }
  }, []); // Empty dependency array ensures this effect only runs once on mount

  const rejectWithShred = async () => {
    try {
      await checkProcessHandler("Reject With Shred");
      //await sendMessage(`Decision: Reject with Shred\n`, {comment: "Decision: Reject with Shred"});

      setIsRejectedWithShred(true);
      navigate("/manage-ticket?page=1");
    } catch (error) {
      toast.error("Oops something went wrong while completing the workflow");
      console.error(error);
    }
  };

  const handleIsRejectedWithShred = () => {
    setIsRejectedWithShred(false);
  };

  const handleNavigation = (selectedOption, direction) => {
    if (selectedOption === "Complete Workflow") {
      navigate(`/manage-ticket/ticket/complete-workflow/${id}`);
      return; // Exit the function to prevent further navigation
    }

    if (selectedOption === "Invest the Check") {
      updateInvestCheckModal(true);
    } else if (selectedOption === "Reject with correspondence") {
      updateRejectWithCorrespondenceModal(true);
    } else if (selectedOption === "Rescan the Check") {
      setIsRescanModalOpen(true); // Open the rescan modal
    }

    const updatedFormData = { ...formData, [step]: selectedOption };
    setFormData(updatedFormData);

    let nextStep;
    if (direction === "next") {
      if (step === REVIEW_ASSIGNE) {
        nextStep = selectedOption === "Check Review" ? INVEST_REJECT : "step3";
      } else if (step === INVEST_REJECT) {
        nextStep =
          selectedOption === "Reject the Check" ? SHRED_CORRESPONDENCE : null;
      }
    } else if (direction === "back") {
      if (step === INVEST_REJECT) nextStep = REVIEW_ASSIGNE;
      else if (step === SHRED_CORRESPONDENCE) nextStep = INVEST_REJECT;
    }

    if (nextStep) {
      const newUrl = `${location.pathname}?step=${nextStep}`;
      navigate(newUrl);
    }
  };

  const handleLockForEdit = async () => {
    try {
      var ticketData = await lockTicketById(id, 'Y');
      setCheckData(ticketData);
      setAssignedTo(ticketData.assignedto)   
      if (ticketData && ticketData.assignedto === currentUserId) {
        setIsAssignedToCurrentUser(true);
        toast.success("You may now Edit this ticket.");
      } else{
        toast.error(`Error locking ticket for edit.`);
      }
    } catch (error) {
      toast.error(`Error locking ticket for edit${error}`);
      console.error(error);
    }
  };

  const handleRescanSubmit = (data) => {
    console.log("Rescan Data Submitted:", data);
    // Handle the form submission for Rescan here
    // You can perform the API call or form processing here
  };

  const renderStep = () => {
    const { loading } = useTicketContext();
    const commonProps = {
      onNext: (option) => handleNavigation(option, "next"),
      onBack: () => handleNavigation("", "back"),
    };

    if(isTicketCompleted) {
      return (
        <div className="font-18-400-24">
          No action available since the ticket is completed.
        </div>
      );
    }else if (!isAssignedToCurrentUser && !loading) {
      return (
<div className="flex flex-col bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200 max-w-md space-y-4">
    {/* Navigation and Action Buttons */}
    <div className="flex justify-between items-center w-full space-x-4">
        {/* Back to Task List Button */}
        <Button type="button" 
      
      onClick={() => navigate("/manage-ticket")}>
              Back to Task List
          </Button> 

        {/* Lock for Edit Button */}
        <button
            type="button"
            onClick={handleLockForEdit}
            disabled={loading}
            className="text-white font-semibold border-none rounded-md transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
                backgroundColor: loading ? "#D1D5DB" : "#58788C",
                color: loading ? "#6B7280" : "#FFFFFF",
                fontFamily: "'Open Sans', sans-serif",
                fontSize: "10px", // Matches font size of the first button
                padding: "6px 12px", // Adjusted padding for consistency
                minWidth: "20px", // Ensures consistent width
                cursor: loading ? "not-allowed" : "pointer",
            }}
            onMouseOver={(e) => {
                if (!loading) e.target.style.backgroundColor = "#486C7A"; // Darken for hover
            }}
            onMouseOut={(e) => {
                if (!loading) e.target.style.backgroundColor = "#58788C"; // Reset hover
            }}
        >
            Lock for Edit
        </button>
    </div>

    {/* Assigned To Info */}
    {assignedTo && (
        <p className="text-gray-800 text-sm">
          <br/>
            This ticket is being worked on by <span className="text-blue-600 font-medium">{assignedTo}</span>
        </p>
    )}
</div>


      );
    }  

    switch (step) {
      case REVIEW_ASSIGNE:
        return (
          <Step1
            {...commonProps}
            selectedOption={formData[REVIEW_ASSIGNE]}
            isCheckReviewAvailable={isCheckReviewAvailable}
            allowedActions={allowedActions} // Pass allowed actions to Step1
          />
        );
      case INVEST_REJECT:
        return (
          <Step2 {...commonProps} selectedOption={formData[INVEST_REJECT]} />
        );
      case SHRED_CORRESPONDENCE:
        return (
          <Step3
            {...commonProps}
            selectedOption={formData[SHRED_CORRESPONDENCE]}
            rejectWithShred={rejectWithShred}
          />
        );
      default:
        return <Step1 {...commonProps} />;
    }
  };

  return (
    <div>
      {/* Back to List Button */}

      <h1 className="font-28-42-400">Review Options</h1>

      {isRejectedWithShred && step === "shred_correspondence" && (
        <ToastItem
          item={"You have chosen to Shred"}
          index={1}
          onRemove={handleIsRejectedWithShred}
        />
      )}
      {renderStep()}

      {isInvestCheckModalOpen && (
        <InvestCheckModal
          onClose={updateInvestCheckModal}
          initialValues={{
            bank: "",
            bankAccount: "",
            checkAmount:
              checkData && checkData.metadata && checkData.metadata.Amount,
          }}
          formConfig={{
            bank: BANK_OPTIONS,
            bankAccountByBank: BANK_ACCOUNTS_BY_BANK,
            bankValidation: (value) => !value && "Please select a Bank.",
            checkAmountValidation: (value) => {
              value = value.trim();
              if (!value) return "Check Amount is required.";

              const amount = parseFloat(value);
              if (isNaN(amount) || amount <= 0)
                return "Check Amount must be greater than 0.";

              const decimalParts = value.split(".");
              if (decimalParts.length > 2 || decimalParts[1] === "")
                return "Check Amount is invalid.";

              if (decimalParts[1] && decimalParts[1].length > 2)
                return "Check Amount must have no more than 2 decimal places.";

              return null;
            },
          }}
        />
      )}

      {isRejectWithCorrespondenceModalOpen && (
        <RejectWithCorrespondingModal
          onClose={updateRejectWithCorrespondenceModal}
        />
      )}

      {isRescanModalOpen && (
        <RescanCheckModal
          onClose={() => setIsRescanModalOpen(false)}
          onSubmit={handleRescanSubmit}
        />
      )}
    </div>
  );
};

export default ReviewProcessFormContainer;

const Radio = ({ options, value, onChange, name }) => (
  <div className="flex flex-col my-2">
    {options.map((option, index) => (
      <div key={index} className="flex items-center justify-start gap-10">
        <input
          type="radio"
          id={`option${index}`}
          className="m-0"
          name={name}
          value={option}
          checked={value === option}
          onChange={onChange}
        />
        <label htmlFor={`option${index}`} className="font-18-400-24 ml-1 m-0">
          {option}
        </label>
      </div>
    ))}
  </div>
);

const Step = ({
  options,
  onNext,
  onBack,
  selectedOption,
  hasBackButton = true,
  rejectWithShred,
}) => {
  let { id } = useParams();
  const { loading } = useTicketContext();

  const [option, setOption] = useState(selectedOption || "");

  useEffect(() => {
    setOption(selectedOption || "");
  }, [selectedOption]);

  const handleChange = (e) => setOption(e.target.value);
  const navigate = useNavigate();

  return (
    <>
      <Radio
        options={options}
        value={option}
        onChange={handleChange}
        name="comment"
      />
      <div className="flex space-x-4">
      <Button type="button" 
      
      onClick={() => navigate("/manage-ticket")}>
              Back to Task List
          </Button>          
        {hasBackButton && (
      <Button
      type="button"
      className=" border-none h-42 font-16-700 w-135 capitalize justify-center br-4 create-btn px-8  text-sm font-semibold text-white"
      onClick={onBack}
      disabled={!option}
    >
      Last Step
    </Button>              

        )}

        {option === "Reject with shred" ? (
          <>
            <Button
              type="button"
              disabled={loading}
              className="border-none h-42 font-16-700 w-135 capitalize justify-center br-4 create-btn px-8 text-sm font-semibold text-white"
              onClick={() => rejectWithShred?.()} // API call for shred
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </>
        ) : (
          <>
        
      <Button
            type="button"
            className=" border-none h-42 font-16-700 w-135 capitalize justify-center br-4 create-btn px-8  text-sm font-semibold text-white"
            onClick={
              option === "Change Assignee" ? undefined : () => onNext(option)
            }
            path={
              option === "Change Assignee"
                ? `/manage-ticket/ticket/edit/${id}/change-assignee`
                : undefined
            }
            disabled={!option}
          >
            Next Step
          </Button>      
          </>

          
        )}
      </div>
    </>
  );
};

// Updated Step1 component to conditionally render options
const Step1 = ({ isCheckReviewAvailable, allowedActions, ...props }) => {
  const availableOptions = ["Change Assignee"];
  
  if (isCheckReviewAvailable) {
    availableOptions.unshift("Check Review");
  }

  // Add "Complete Workflow" if it's present in allowedActions
  if (allowedActions && allowedActions.includes("Complete Workflow")) {
    availableOptions.push("Complete Workflow");
  }

  return <Step options={availableOptions} hasBackButton={false} {...props} />;
};

const Step2 = (props) => (
  <Step options={["Invest the Check", "Reject the Check"]} {...props} />
);

// Step3 now includes "Rescan the Check"
const Step3 = (props) => (
  <Step
    options={["Reject with shred", "Reject with correspondence", "Rescan the Check"]}
    {...props}
  />
);
